import { useEffect, useState } from 'react';
import { Card, Col, Container, Row, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBell,
    faRefresh,
} from '@fortawesome/free-solid-svg-icons';
import Loader from 'react-spinner-loader';
import { CandidateService } from '../../../../services/candidateService';
import './jobNotificationBatches.css';

const JobNotificationBatches = () => {
    const [batches, setBatches] = useState([]);
    const [pageNo, setPageNo] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [loader, setLoader] = useState(false);

    const fetchBatches = async () => {
        setLoader(true);

        try {
            const response =
                await CandidateService.getNotificationBatches(
                    pageNo,
                    pageSize
                );

            const data = response?.data;

            setBatches(data?.batches || []);
            setTotalCount(data?.totalCount || 0);
            setPageCount(data?.pageCount || 0);
            setHasNext(Boolean(data?.hasNext));
        } catch (error) {
            console.error(
                'Error fetching job notification batches:',
                error
            );

            setBatches([]);
            setTotalCount(0);
            setPageCount(0);
            setHasNext(false);
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchBatches();
    }, [pageNo, pageSize]);


    const formatJobDate = (date) => {
        if (!date) return '-';

        const parsedDate = new Date(Number(date));

        if (Number.isNaN(parsedDate.getTime())) return '-';

        return parsedDate.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const formatNotificationDateTime = (date) => {
        if (!date) return '-';

        const parsedDate = new Date(Number(date));

        if (Number.isNaN(parsedDate.getTime())) return '-';

        return parsedDate.toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };
    const getStatusClass = (status) => {
        switch ((status || '').toUpperCase()) {
            case 'SUCCESS':
            case 'COMPLETED':
                return 'status-success';

            case 'FAILED':
                return 'status-failed';

            case 'PENDING':
                return 'status-pending';

            case 'IN_PROGRESS':
            case 'PROCESSING':
                return 'status-processing';

            default:
                return 'status-default';
        }
    };

    const renderPageNumbers = () => {
        const currentPage = pageNo + 1;
        const pageNumbers = [];

        if (pageCount <= 0) {
            return pageNumbers;
        }

        pageNumbers.push(1);

        if (pageCount > 5) {
            if (currentPage > 3) {
                pageNumbers.push('...');
            }

            const start = Math.max(2, currentPage - 1);
            const end = Math.min(
                pageCount - 1,
                currentPage + 1
            );

            for (let i = start; i <= end; i++) {
                pageNumbers.push(i);
            }

            if (currentPage < pageCount - 2) {
                pageNumbers.push('...');
            }

            pageNumbers.push(pageCount);
        } else {
            for (let i = 2; i <= pageCount; i++) {
                pageNumbers.push(i);
            }
        }

        return pageNumbers;
    };

    const renderPagination = () => {
        if (pageCount <= 0) {
            return null;
        }

        return (
            <div className="pagination-controls mt-3">
                <Row className="align-items-center g-2 flex-nowrap">

                    <Col xs="auto">
                        <div className="pagination-container d-flex gap-1">

                            <button
                                type="button"
                                className="border rounded p-1"
                                onClick={() => setPageNo(0)}
                                disabled={pageNo === 0 || loader}
                            >
                                {'<<'}
                            </button>

                            <button
                                type="button"
                                className="border rounded p-1"
                                onClick={() =>
                                    setPageNo((currentPage) =>
                                        Math.max(0, currentPage - 1)
                                    )
                                }
                                disabled={pageNo === 0 || loader}
                            >
                                {'<'}
                            </button>

                            {renderPageNumbers().map(
                                (page, index) => (
                                    <button
                                        type="button"
                                        key={`${page}-${index}`}
                                        className={`border rounded p-1 ${pageNo + 1 === page
                                                ? 'active'
                                                : ''
                                            }`}
                                        onClick={() => {
                                            if (
                                                typeof page ===
                                                'number'
                                            ) {
                                                setPageNo(
                                                    page - 1
                                                );
                                            }
                                        }}
                                        disabled={
                                            typeof page !==
                                            'number' ||
                                            loader
                                        }
                                    >
                                        {page}
                                    </button>
                                )
                            )}

                            <button
                                type="button"
                                className="border rounded p-1"
                                onClick={() =>
                                    setPageNo(
                                        (currentPage) =>
                                            currentPage + 1
                                    )
                                }
                                disabled={
                                    !hasNext ||
                                    loader
                                }
                            >
                                {'>'}
                            </button>

                            <button
                                type="button"
                                className="border rounded p-1"
                                onClick={() =>
                                    setPageNo(
                                        pageCount - 1
                                    )
                                }
                                disabled={
                                    !hasNext ||
                                    loader
                                }
                            >
                                {'>>'}
                            </button>

                        </div>
                    </Col>

                    <Col xs="auto">
                        <div className="d-flex align-items-center gap-2">
                            <span
                                style={{
                                    fontSize: '15px',
                                }}
                            >
                                Go to page:
                            </span>

                            <input
                                type="number"
                                value={pageNo + 1}
                                onChange={(event) => {
                                    const input =
                                        event.target.value;

                                    if (!input) {
                                        return;
                                    }

                                    const page =
                                        Number(input);

                                    if (
                                        Number.isNaN(page)
                                    ) {
                                        return;
                                    }

                                    const validPage =
                                        Math.min(
                                            Math.max(
                                                1,
                                                page
                                            ),
                                            pageCount
                                        );

                                    setPageNo(
                                        validPage - 1
                                    );
                                }}
                                className="border p-1 rounded"
                                style={{
                                    width: '3rem',
                                }}
                                min="1"
                                max={pageCount}
                                disabled={loader}
                            />
                        </div>
                    </Col>

                    <Col xs="auto">
                        <div className="d-flex align-items-center gap-2">
                            <span
                                style={{
                                    fontSize: '15px',
                                }}
                            >
                                Show
                            </span>

                            <select
                                value={pageSize}
                                onChange={(event) => {
                                    setPageSize(
                                        Number(
                                            event.target.value
                                        )
                                    );
                                    setPageNo(0);
                                }}
                                className="border p-1 rounded"
                                disabled={loader}
                            >
                                {[10, 20, 30, 40, 50].map(
                                    (size) => (
                                        <option
                                            key={size}
                                            value={size}
                                        >
                                            {size}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                    </Col>

                </Row>
            </div>
        );
    };

    return (
        <Container
            fluid
            className="job-notification-container"
        >
            <Loader
                show={loader}
                type="body"
                stack="vertical"
                message="Loading Data"
            />

            <Row>
                <Col>
                    <Card className="job-notification-card">

                        <Card.Header className="job-notification-header">
                            <div className="job-notification-title">
                                <div className="job-notification-icon">
                                    <FontAwesomeIcon
                                        icon={faBell}
                                    />
                                </div>

                                <div>
                                    <h5>
                                        Job Notification Batches
                                    </h5>

                                    <small>
                                        View weekly job
                                        notification runs
                                    </small>
                                </div>
                            </div>

                            <button
                                type="button"
                                className="job-notification-refresh"
                                onClick={fetchBatches}
                                disabled={loader}
                            >
                                <FontAwesomeIcon
                                    icon={faRefresh}
                                />
                                Refresh
                            </button>
                        </Card.Header>

                        <Card.Body>
                            {totalCount > 0 && (
                                <div className="total-batches-count">
                                    Total batches: {totalCount}
                                </div>
                            )}
                            <div className="table-responsive">
                                <Table
                                    bordered
                                    hover
                                    className="job-notification-table align-middle"
                                >
                                    <thead>

                                        <tr>
                                            <th>S.No</th>
                                            <th>Batch ID</th>
                                            <th>Status</th>
                                            <th>Total</th>
                                            <th>Sent</th>
                                            <th>Failed</th>
                                            <th>Pending</th>
                                            <th>
                                                Retry Count
                                            </th>
                                            <th>
                                                Notification Sent At
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {batches.length === 0 ? (
                                            <tr>
                                                <td
                                                    colSpan="11"
                                                    className="empty-message"
                                                >
                                                    No job notification
                                                    batches found.
                                                </td>
                                            </tr>
                                        ) : (
                                            batches.map(
                                                (
                                                    batch,
                                                    index
                                                ) => (
                                                    <tr
                                                        key={
                                                            batch.batchId
                                                        }
                                                    >
                                                        <td>
                                                            {pageNo *
                                                                pageSize +
                                                                index +
                                                                1}
                                                        </td>

                                                        <td className="batch-id">
                                                            {
                                                                batch.batchId
                                                            }
                                                        </td>

                                                        <td>
                                                            <span
                                                                className={`status-badge ${getStatusClass(
                                                                    batch.status
                                                                )}`}
                                                            >
                                                                {
                                                                    batch.status
                                                                }
                                                            </span>
                                                        </td>

                                                        <td>
                                                            {
                                                                batch.totalCount ??
                                                                0
                                                            }
                                                        </td>

                                                        <td className="sent-count">
                                                            {
                                                                batch.sentCount ??
                                                                0
                                                            }
                                                        </td>

                                                        <td className="failed-count">
                                                            {
                                                                batch.failedCount ??
                                                                0
                                                            }
                                                        </td>

                                                        <td className="pending-count">
                                                            {
                                                                batch.pendingCount ??
                                                                0
                                                            }
                                                        </td>

                                                        <td>
                                                            {
                                                                batch.retryCount ??
                                                                0
                                                            }
                                                        </td>

                                                        <td>
                                                            {formatNotificationDateTime(
                                                                batch.lastUpdated
                                                            )}
                                                        </td>
                                                    </tr>
                                                )
                                            )
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            <div className="pagination-container">
                                {renderPagination()}
                            </div>



                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default JobNotificationBatches;